import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowRight, CheckCircle2, Mail } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";
import api, { formatApiError } from "@/lib/api";
import { pushFormEvent } from "@/lib/analytics";
import { AUTH } from "@/constants/testIds";
import { BrandLogo } from "@/components/BrandLogo";

const BENEFITS = [
  "Referral commissions on eligible tracked bookings",
  "Purchase AWARE kits to keep on hand",
  "Secure Partner Portal & professional reports",
  "Partner branding on Summary Reports (where configured)",
  "Retail Stockist opportunity for selected businesses",
];

const INTEREST_OPTIONS = [
  { key: "refer_customers", label: "Referring customers" },
  { key: "purchase_kits", label: "Purchasing AWARE kits" },
  { key: "arrange_testing", label: "Arranging testing for customers" },
  { key: "retail_stockist", label: "Becoming a Retail Stockist" },
];

export default function Register() {
  const [form, setForm] = useState({
    email: "", password: "", full_name: "", company_name: "", phone: "",
  });
  const [interests, setInterests] = useState([]);
  const [inviteToken, setInviteToken] = useState(null);
  const [invitedBusiness, setInvitedBusiness] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const { register } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    const token = new URLSearchParams(window.location.search).get("invite");
    if (!token) return;
    api.get(`/partner-invites/${token}`)
      .then(({ data }) => {
        setInviteToken(token);
        setInvitedBusiness(data.business_name || null);
        setForm((f) => ({
          ...f,
          company_name: f.company_name || data.business_name || "",
          full_name: f.full_name || data.contact_name || "",
          email: f.email || data.email || "",
        }));
      })
      .catch(() => {});
  }, []);

  const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const toggleInterest = (key) =>
    setInterests((prev) => prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]);

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await register({ ...form, requested_capabilities: interests, invite_token: inviteToken || undefined });
      toast.success("Application received. We'll review shortly.");
      pushFormEvent("registration_form_submit", {
        form_name: "trade_partner_register",
        form_type: "registration",
        registration_type: "trade_partner",
        capability_interest: interests.join(",") || null,
      });
      navigate("/trade/pending");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col">
      <header className="bg-white border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <BrandLogo to="/" size="sm" />
        </div>
      </header>

      <main className="flex-1 px-4 py-10 lg:py-16">
        <div className="max-w-5xl mx-auto grid lg:grid-cols-12 gap-10">
          <div className="lg:col-span-5">
            <div className="text-[11px] uppercase tracking-[0.2em] font-bold text-cyan-600">AWARE Partner Network</div>
            <h1 className="font-display mt-2 text-3xl lg:text-4xl font-bold text-slate-900 leading-[1.1]">
              Apply to Become an AWARE Partner
            </h1>
            <p className="mt-3 text-slate-600">
              Plumbers, filtration and pump specialists, tank and bore contractors, rural suppliers
              and property professionals across Australia.
            </p>
            <p className="mt-2 text-sm text-slate-500">
              Applications are reviewed before Partner access is activated.{" "}
              <Link to="/partners" data-testid="register-partner-program-link" className="font-semibold text-cyan-600 hover:underline">
                Learn about the Partner Program
              </Link>
            </p>
            {inviteToken && (
              <div data-testid="register-invite-banner" className="mt-4 rounded-2xl bg-cyan-50 border border-cyan-200 px-4 py-3 flex items-start gap-2.5">
                <Mail className="w-4 h-4 text-cyan-600 mt-0.5 flex-shrink-0" />
                <p className="text-sm text-cyan-900">
                  You've been invited to join the AWARE Partner Network
                  {invitedBusiness ? <> — welcome, <span className="font-semibold">{invitedBusiness}</span>!</> : "!"}{" "}
                  We've pre-filled your details below.
                </p>
              </div>
            )}
            <div className="mt-7 space-y-3">
              {BENEFITS.map((b) => (
                <div key={b} className="flex items-start gap-2.5">
                  <CheckCircle2 className="w-5 h-5 mt-0.5 text-cyan-500 flex-shrink-0" />
                  <span className="text-sm font-medium text-slate-800">{b}</span>
                </div>
              ))}
            </div>
            <div className="mt-8 p-4 rounded-2xl bg-slate-900 text-white">
              <div className="text-[10px] uppercase tracking-[0.2em] text-cyan-400 font-bold mb-1">How it works</div>
              <p className="text-sm text-slate-200 leading-relaxed">
                Submit your application below. Our trade team reviews within 1 business day and approves verified businesses.
              </p>
            </div>
          </div>

          <div className="lg:col-span-7">
            <form onSubmit={submit} className="bg-white rounded-3xl border border-slate-200 p-7 lg:p-9 shadow-sm">
              <h2 className="font-display text-2xl font-bold text-slate-900">Apply for trade access</h2>
              <p className="mt-1 text-sm text-slate-500">All fields required unless marked optional.</p>

              <div className="mt-6 grid sm:grid-cols-2 gap-4">
                <div className="sm:col-span-2">
                  <Label className="text-xs uppercase tracking-wider font-semibold text-slate-700">Full Name</Label>
                  <Input data-testid={AUTH.registerFullName} required value={form.full_name} onChange={update("full_name")} className="mt-1.5 h-12 rounded-xl" placeholder="Jane Smith" />
                </div>
                <div className="sm:col-span-2">
                  <Label className="text-xs uppercase tracking-wider font-semibold text-slate-700">Company / Trading Name</Label>
                  <Input data-testid={AUTH.registerCompany} required value={form.company_name} onChange={update("company_name")} className="mt-1.5 h-12 rounded-xl" placeholder="Acme Pump Shop QLD" />
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-wider font-semibold text-slate-700">Work Email</Label>
                  <Input data-testid={AUTH.registerEmail} type="email" required value={form.email} onChange={update("email")} className="mt-1.5 h-12 rounded-xl" placeholder="jane@acmepump.com.au" />
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-wider font-semibold text-slate-700">Phone <span className="normal-case text-slate-400">(optional)</span></Label>
                  <Input data-testid={AUTH.registerPhone} value={form.phone} onChange={update("phone")} className="mt-1.5 h-12 rounded-xl" placeholder="04XX XXX XXX" />
                </div>
                <div className="sm:col-span-2">
                  <Label className="text-xs uppercase tracking-wider font-semibold text-slate-700">Password</Label>
                  <Input data-testid={AUTH.registerPassword} type="password" required minLength={12} value={form.password} onChange={update("password")} className="mt-1.5 h-12 rounded-xl" placeholder="At least 12 characters" />
                </div>
                <div className="sm:col-span-2">
                  <Label className="text-xs uppercase tracking-wider font-semibold text-slate-700">
                    How would you like to work with AWARE? <span className="normal-case text-slate-400">(optional)</span>
                  </Label>
                  <div className="mt-2 grid sm:grid-cols-2 gap-2" data-testid="register-interests">
                    {INTEREST_OPTIONS.map(({ key, label }) => {
                      const on = interests.includes(key);
                      return (
                        <button
                          key={key}
                          type="button"
                          data-testid={`register-interest-${key}`}
                          onClick={() => toggleInterest(key)}
                          className={`flex items-center gap-2 px-3.5 py-2.5 rounded-xl border text-sm font-medium text-left transition-colors ${
                            on
                              ? "border-cyan-500 bg-cyan-50 text-cyan-800"
                              : "border-slate-200 bg-white text-slate-600 hover:border-slate-300"
                          }`}
                        >
                          <CheckCircle2 className={`w-4 h-4 flex-shrink-0 ${on ? "text-cyan-600" : "text-slate-300"}`} />
                          {label}
                        </button>
                      );
                    })}
                  </div>
                  <p className="mt-2 text-[11px] text-slate-400">
                    Expression of interest only — Partner capabilities are assigned by AWARE after
                    your application is reviewed.
                  </p>
                </div>
              </div>

              <Button data-testid={AUTH.registerSubmit} disabled={submitting} type="submit" size="lg" className="w-full mt-7 btn-aqua rounded-full font-semibold">
                {submitting ? "Submitting..." : <>Submit application <ArrowRight className="w-4 h-4 ml-1.5" /></>}
              </Button>

              <p className="mt-5 text-center text-xs text-slate-500">
                Already a member? <Link to="/trade/login" className="font-semibold text-cyan-600 hover:underline">Sign in</Link>
              </p>
            </form>
          </div>
        </div>
      </main>
    </div>
  );
}
